import { useState, useEffect } from "react";
import { useEscKey } from "src/hooks/useEscKey";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { TbX, TbCloudUpload } from "react-icons/tb";
import { apiRequest } from "src/services/apiClient";
import InvoiceStatus from "src/components/InvoiceStatus/InvoiceStatus";

interface UploadInvoice {
    id: string;
    readableId: string;
    patient: string;
    recipientId?: string;
    patientId?: string;
    creationDate: string;
    amount: number;
    fileName: string;
    padId?: string;
    uploadDate?: string;
    status: number;
    dunning?: number;
    sentDate?: string;
}

interface Props {
    onClose: () => void;
}

export default function UploadHistoryPopup({ onClose }: Props) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    useEscKey(onClose, true);
    const [invoices, setInvoices] = useState<UploadInvoice[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        apiRequest<{ invoices: UploadInvoice[] }>("/uploads")
            .then((data) => setInvoices(data?.invoices ?? []))
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, []);

    const rows = [...invoices].sort((a, b) =>
        (b.uploadDate || b.creationDate).localeCompare(a.uploadDate || a.creationDate));

    const fmt = (d: string) =>
        new Date(d).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });

    return (
        <div className="upload-hist-overlay" onClick={onClose}>
            <div className="upload-hist-modal upload-hist-modal--wide" onClick={(e) => e.stopPropagation()}>
                <div className="upload-hist-modal__header">
                    <span className="upload-hist-modal__title">
                        <TbCloudUpload /> {t("invoices.uploadHistoryTitle")}
                    </span>
                    <button className="upload-hist-modal__close" onClick={onClose}>
                        <TbX />
                    </button>
                </div>

                <div className="upload-hist-modal__body">
                    {loading && (
                        <div className="upload-hist-modal__state">
                            <span className="upload-hist-modal__spinner" />
                        </div>
                    )}
                    {error && (
                        <div className="upload-hist-modal__state upload-hist-modal__state--error">{error}</div>
                    )}
                    {!loading && !error && rows.length === 0 && (
                        <div className="upload-hist-modal__state">{t("invoices.uploadHistoryEmpty")}</div>
                    )}
                    {!loading && !error && rows.length > 0 && (
                        <table className="upload-hist-table">
                            <thead>
                                <tr>
                                    <th>{t("invoices.uploadHistoryFile")}</th>
                                    <th className="upload-hist-table__col-date">{t("invoices.uploadHistoryDate")}</th>
                                    <th>{t("invoices.uploadHistoryPatient")}</th>
                                    <th className="upload-hist-table__col-amount">{t("invoices.uploadHistoryAmount")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((inv) => (
                                    <tr
                                        key={inv.id}
                                        className="upload-hist-table__invoice-row"
                                        onClick={() => { onClose(); navigate(`/invoices/${inv.id}`); }}
                                    >
                                        <td className="upload-hist-table__file-cell">
                                            <span className="upload-hist-table__file-name" title={inv.fileName ?? "—"}>
                                                {inv.fileName ?? "—"}
                                            </span>
                                            <span className="upload-hist-table__readable-id">{inv.readableId}</span>
                                        </td>
                                        <td className="upload-hist-table__date">
                                            {(inv.uploadDate || inv.creationDate) ? fmt(inv.uploadDate || inv.creationDate) : "—"}
                                        </td>
                                        <td className="upload-hist-table__patient-name">{inv.patient || "—"}</td>
                                        <td className="upload-hist-table__amount">
                                            <div className="upload-hist-table__inv-row">
                                                <span>€{(inv.amount ?? 0).toFixed(2)}</span>
                                                <InvoiceStatus status={inv.status} dunning={inv.dunning} unsent={!inv.sentDate} />
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                <div className="upload-hist-modal__footer">
                    <button className="upload-hist-modal__ok" onClick={onClose}>
                        {t("popup.ok")}
                    </button>
                </div>
            </div>
        </div>
    );
}
